import serviceHandler from "@/helpers/handlers/service";
import facilitiesService from "@/services/facilities";
import { FacilityModel } from "@/utils/google/facilities/facility-model";
import { FacilitiesExportFactory } from "@/utils/google/facilities/facilities-export";

const createFacilityModel = (facility, itemIndex) => {
  const model = new FacilityModel();
  model.makeFacilityModel({ facility, itemIndex });
  return model.returnFacilityModel();
};

export default {
  resetStore({ commit }) {
    commit("RESET_STORE");
  },

  async initFacilities(
    { dispatch },
    {
      page = 1,
      perPage,
      query = null,
      isOwner = true,
      allFacilitiesIsOwner = isOwner, // variable to prevent checking facilities with no `isOwner` flag set to true
      isVerified = false,
      externalChangesStatuses = [],
      externalChangesSources = [],
      cities = [],
      facilitiesIds = [],
      groups = [],
      locationState = null,
      openStatus = null,
      bingLocationStatus = null,
      appleLocationStatus = null,
      withoutGmbAddress = false,
      fetchAllFacilitiesIds = false
    }
  ) {
    // Otherwise `fetchFacilities` fetches the filtered ids, which would overwrite these anyway.
    if (!fetchAllFacilitiesIds) {
      await dispatch("fetchAllFacilitiesIds", {
        isOwner: allFacilitiesIsOwner,
        externalChangesStatuses,
        externalChangesSources,
        isVerified
      });
    }
    await dispatch("fetchFacilities", {
      page,
      perPage,
      query,
      isOwner,
      allFacilitiesIsOwner,
      isVerified,
      externalChangesStatuses,
      externalChangesSources,
      cities,
      facilitiesIds,
      groups,
      locationState,
      openStatus,
      bingLocationStatus,
      appleLocationStatus,
      withoutGmbAddress,
      fetchAllFacilitiesIds
    });
    dispatch("selectFacilities", []);
  },

  async fetchFacilities(
    { dispatch, commit },
    {
      page,
      perPage,
      query = null,
      isOwner = true,
      allFacilitiesIsOwner = isOwner,
      isVerified = false,
      externalChangesStatuses = [],
      externalChangesSources = [],
      externalChangesFields = [],
      cities = [],
      facilitiesIds = [],
      groups = [],
      locationState = null,
      openStatus = null,
      bingLocationStatus = null,
      appleLocationStatus = null,
      withoutGmbAddress = false,
      fetchAllFacilitiesIds = false // condition needed for `check all` functionallity on facilities lists with filtering
    }
  ) {
    const [facilities] = await Promise.all([
      serviceHandler(
        facilitiesService.getFacilitiesData({
          page,
          perPage,
          query,
          is_owner: isOwner,
          is_verified: isVerified,
          external_changes_status: externalChangesStatuses,
          external_changes_source: externalChangesSources,
          external_changes_fields: externalChangesFields,
          cities: cities,
          facilities_ids: facilitiesIds,
          groups: groups,
          location_state: locationState,
          open_status: openStatus,
          bing_status: bingLocationStatus,
          apple_status: appleLocationStatus,
          without_gmb_address: withoutGmbAddress
        })
      ),
      fetchAllFacilitiesIds &&
        dispatch("fetchAllFacilitiesIds", {
          query,
          isOwner: allFacilitiesIsOwner,
          isVerified,
          externalChangesStatuses,
          externalChangesSources,
          externalChangesFields,
          facilitiesIds,
          cities: cities,
          groups,
          locationState,
          openStatus,
          bingLocationStatus,
          appleLocationStatus
        })
    ]);

    const data = facilities.results.map((facility, index) =>
      createFacilityModel(facility, index + 1 + (page - 1) * perPage)
    );
    commit("SET_CURRENT_PAGE", data);
    commit("SET_TOTAL_COUNT", facilities.count);
  },

  async fetchAllFacilitiesIds(
    { commit },
    {
      isVerified = false,
      query = null,
      externalChangesStatuses = [],
      externalChangesSources = [],
      externalChangesFields = [],
      facilitiesIds = [],
      cities = [],
      groups = [],
      locationState = null,
      openStatus = null,
      bingLocationStatus = null,
      appleLocationStatus = null,
      isOwner = false
    }
  ) {
    const ids = await serviceHandler(
      facilitiesService.getFacilitiesIds({
        query,
        is_owner: isOwner,
        is_verified: isVerified,
        external_changes_status: externalChangesStatuses,
        external_changes_source: externalChangesSources,
        external_changes_fields: externalChangesFields,
        cities: cities,
        facilities_ids: facilitiesIds,
        groups: groups,
        location_state: locationState,
        open_status: openStatus,
        bing_status: bingLocationStatus,
        apple_status: appleLocationStatus
      })
    );
    commit("SET_ALL_FACILITIES_IDS", ids);
  },

  async fetchCities({ commit }) {
    const result = await serviceHandler(
      facilitiesService.getFacilitiesCities()
    );
    const cities = result.map(({ city }) => city);
    commit("SET_CITIES", cities);
  },

  toggleFacility({ commit, state, getters }, facility) {
    const selected = [...state.selectedFacilities];
    const foundIndex = selected.findIndex(({ id }) => {
      return id === facility.id;
    });

    if (getters.isFacilityInitiallySelected(facility.id)) return; // to disable toggling initially selected facilities (add to group dialog)

    if (foundIndex !== -1) {
      selected.splice(foundIndex, 1);
    } else {
      const foundFacilityIds = state.allFacilitiesIds.find(
        ({ id }) => facility.id === id
      );
      if (foundFacilityIds) selected.push(foundFacilityIds);
    }

    commit("SET_SELECTED_FACILITIES", selected);
  },

  toggleAllFacilities({ commit, state, getters }) {
    if (!getters.isAllFacilitiesSelected) {
      commit("SET_SELECTED_FACILITIES", [
        ...state.allFacilitiesIds,
        ...state.initiallySelectedFacilities.filter(
          ({ id }) => !state.allFacilitiesIds.map(({ id }) => id).includes(id)
        )
      ]);
    } else {
      commit("SET_SELECTED_FACILITIES", [...state.initiallySelectedFacilities]);
    }
  },

  toggleAllCurrentPageFacilities({ commit, state, getters }) {
    const selected = state.selectedFacilities;
    const currentPageFacilitesIds = state.currentPageFacilities
      .filter(facility => facility.facilityListings.isOwner)
      .map(({ id }) => id);

    const currentPage = state.allFacilitiesIds.filter(({ id }) =>
      currentPageFacilitesIds.includes(id)
    );

    if (!getters.isAllCurrentPageFacilitiesSelected) {
      currentPage.forEach(currentItem => {
        if (getters.isFacilityInitiallySelected(currentItem.id)) return; // to disable toggling initially selected facilities (add to group dialog)

        if (selected.findIndex(({ id }) => id === currentItem.id) === -1) {
          selected.push(currentItem);
        }
      });
    } else {
      currentPage.forEach(currentItem => {
        if (getters.isFacilityInitiallySelected(currentItem.id)) return; // to disable toggling initially selected facilities (add to group dialog)

        const selectedIndex = selected.findIndex(
          ({ id }) => id === currentItem.id
        );

        if (selectedIndex > -1) {
          selected.splice(selectedIndex, 1);
        }
      });
    }

    commit("SET_SELECTED_FACILITIES", selected);
  },

  selectFacilities({ commit, state }, facilities = state.allFacilitiesIds) {
    const selected = [...facilities];
    commit("SET_SELECTED_FACILITIES", selected);
  },

  selectInitiallySelectedFacilities({ commit }, facilities) {
    commit("SET_INITIALLY_SELECTED_FACILITIES", facilities);
  },

  selectFacilitiesByGmbAddressIds({ state, dispatch }, gmbAddressIds) {
    const selected = state.allFacilitiesIds.filter(({ gmb_address_id }) =>
      gmbAddressIds.includes(gmb_address_id)
    );
    dispatch("selectFacilities", selected);
  },

  selectFacilitiesByFacilitiesIds({ state, dispatch }, facilitiesIds) {
    const selected = state.allFacilitiesIds.filter(({ id }) =>
      facilitiesIds.includes(id)
    );
    dispatch("selectFacilities", selected);
  },

  async exportAndDownloadSelectedFacilities({ getters }, fileType) {
    const exportFactory = new FacilitiesExportFactory();
    await exportFactory.makeExport({
      fileType,
      ids: getters.selectedFacilitiesIds
    });
    exportFactory.downloadFile();
  }
};
